import {useContext, useEffect, useRef} from "react";
import {useParams} from "react-router-dom";

import {format} from "date-fns";

// Models
import {HeadingType} from "Models/Enums/HeadingType";
import {PopUpType} from "Models/Enums/PopUpType";
import {ContentType} from "Models/Enums/ContentType";

// Context
import {UserAuthenticationContext} from "../../../Context/UserAuthenticationContext";

// Helpers
import {IsAuthenticated} from "../../../Helpers/Account/Account";
import {SetTitle} from "../../../Helpers/PageMetadata";
import {GetArticleShareUrl} from "../../../Helpers/Read/Articles";

// Hooks
import useFetchSingleArticle from "../../../Hooks/Content/Read/useFetchSingleArticle";

// Components
import SharingMetatags from "Components/Head/SharingMetatags";
import Heading from "Components/UI/Text/Heading";
import PopUp from "Components/UI/Modals/PopUp";
import ArticleGridItemSingle from "Components/Layouts/Content/Read/ArticleGridItemSingle";
import {NextAricle} from "Components/Layouts/Content/Read/NextArticle";
import ArticleHeader from "Components/Layouts/Content/Read/ArticleHeader";
import ArticleContent from "Components/Layouts/Content/Read/ArticleContent";
import ArticleComments from "../../../Components/ArticleComments";
import {ArticleCategoryTerm} from "Components/Buttons/ArticleCategoryTerm";
import {SocialsMedia} from "Components/UI/SocialsMedia";

import useFetchRelatedArticles from "../../../Hooks/Content/Read/useFetchRelatedArticles";

const ArticleSingle = () => {
    const {id} = useParams();
    const articleId = parseInt(id || "0", 10);
    const {userData} = useContext(UserAuthenticationContext);
    const commentsRef = useRef<HTMLDivElement>(null);

    const {article, loading, error} = useFetchSingleArticle(articleId);

    // Fetch the 5 preceding articles in the same category
    const {relatedData, loadingRelated} = useFetchRelatedArticles(
        article?.categories?.[0]?.id,
        article?.id,
        article?.releaseDate
    );

    // Slice the payload: 1 for Next, up to 4 for Suggested
    const nextArticle = relatedData?.items?.[0] || null;
    const suggestedArticles = relatedData?.items?.slice(1, 5) || [];

    // Set Document Title
    useEffect(() => {
        if (article?.title) {
            SetTitle(article.title);
        }
    }, [article]);

    const calculateReadTime = (htmlContent: string) => {
        if (!htmlContent) return 0;
        const text = htmlContent.replace(/<[^>]+>/g, ""); // Strip HTML tags
        const wordCount = text.split(/\s+/).length;
        return Math.ceil(wordCount / 200);
    };

    if (loading) {
        return <div className="page page--read">Loading...</div>; // Optional loading state
    }

    if (error || !article) {
        return <div className="page page--read" role="status">{error || "This article is currently unavailable."}</div>;
    }

    return (
        <div className="article-single is-light-mode">
            {!IsAuthenticated(userData) && (
                <PopUp
                    canBeDismissed={true}
                    apiEnum={PopUpType.Read}
                    isFullScreen={true}
                />
            )}

            { /** Meta Tags **/}
            {article !== null && (
                <SharingMetatags
                    title={article.title ?? "Untitled article"}
                    description={article.subTitle ?? ""}
                    url={GetArticleShareUrl(article.urlTitle, article.id)}
                    image={article.header ?? ""}
                    type="article"
                />
            )}

            { /** Article Header  **/}
            <ArticleHeader
                article={article} // Note: Make sure ArticleHeader accepts ArticleDTO now!
                ReadTime={article?.description ? calculateReadTime(article.description) : 0}
                DateToString={article?.releaseDate ? format(new Date(article.releaseDate), "do MMMM yyyy") : ""}
                commentsRef={commentsRef}
            />

            { /** Article Content  **/}
            <div className="article-single__body">
                <div className="article-content">
                    {/* The new API returns the HTML content inside the description property */}
                    {article?.description && (
                        <ArticleContent html={article.description} />
                    )}
                </div>

                <div className="article-content__meta-container">
                    { /** Static tag list **/}
                    { /** <TagsList /> **/}
                    {article !== null && (
                        <SocialsMedia
                            isShare={true}
                            title={article.title ?? "Untitled article"}
                            contentType={ContentType.Article}
                            articleUrlTitle={article.urlTitle}
                            articleId={article.id}
                        />
                    )}
                </div>

                <div id="article-comments" className="article-comments" ref={commentsRef} tabIndex={-1}>
                    <ArticleComments key={article.id} articleId={article.id} />
                </div>
            </div>

            { /** Article Highlights - Next Article, Suggested Read**/}
            <div className="article-single__footer">
                <div className="article-highlights__section">

                    {!loadingRelated && nextArticle && (
                        <NextAricle article={nextArticle}/>
                    )}

                    {!loadingRelated && suggestedArticles.length > 0 && (
                        <div className="article-single__grid">
                            <div className="suggested-article__header">
                                <Heading type={HeadingType.H3}>
                                    Latest in{" "}
                                    {article?.categories?.[0]?.title || "Global Current Events"}
                                </Heading>

                                <ArticleCategoryTerm
                                    category={article?.categories?.[0]}
                                />
                            </div>

                            <div className="suggested-article__container">
                                {suggestedArticles.map((sArticle) => (
                                    <ArticleGridItemSingle
                                        article={sArticle}
                                        key={sArticle?.id}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ArticleSingle;
